import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Input, Label } from "reactstrap";
import Select from "react-select";
import { customFieldsCopy, fieldTypeLabels, unitSuggestions } from "./copy";
import type {
  TaxonomyDefinition,
  TaxonomyFieldType,
  TaxonomySource,
  TaxonomyUsedIn,
  TaxonomyVisibility,
} from "./types";
import TaxonomyOptionEditor, { DraftOption } from "./TaxonomyOptionEditor";
import { findExactNameMatch, findNearNameMatches, isSelectType, trimTaxonomyInput } from "./values";

type FormState = {
  id?: string;
  name: string;
  description: string;
  fieldType: TaxonomyFieldType;
  usedIn: TaxonomyUsedIn;
  options: DraftOption[];
  unit: string;
  tags: string;
  requiredOnPublish: boolean;
  visibility: TaxonomyVisibility;
  source?: TaxonomySource;
};

const emptyForm = (): FormState => ({
  name: "",
  description: "",
  fieldType: "TEXT",
  usedIn: "PRODUCT",
  options: [{ label: "", active: true }],
  unit: "",
  tags: "",
  requiredOnPublish: false,
  visibility: "INTERNAL",
});

type Props = {
  open: boolean;
  mode: "add" | "edit";
  initial?: Partial<FormState>;
  definitions: TaxonomyDefinition[];
  saving?: boolean;
  error?: string;
  onClose: () => void;
  onSave: (form: FormState) => void;
  onUseExisting?: (definition: TaxonomyDefinition) => void;
};

const typeOptions = (Object.keys(fieldTypeLabels) as TaxonomyFieldType[]).map((value) => ({
  value,
  label: fieldTypeLabels[value],
}));

function useIsMobile(max = 767) {
  const [mobile, setMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= max
  );
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth <= max);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [max]);
  return mobile;
}

const TaxonomyFieldOverlay = ({
  open,
  mode,
  initial,
  definitions,
  saving,
  error,
  onClose,
  onSave,
  onUseExisting,
}: Props) => {
  const mobile = useIsMobile();
  const [viewport, setViewport] = useState<{ height: number; top: number }>();
  const [form, setForm] = useState<FormState>(emptyForm());
  const panelRef = useRef<HTMLDivElement | null>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const savingRef = useRef(saving);
  savingRef.current = saving;
  const firstRef = useRef<HTMLInputElement | null>(null);
  const previousFocus = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    setForm({
      ...emptyForm(),
      ...initial,
      options: initial?.options?.length ? initial.options : [{ label: "", active: true }],
    });
    const t = window.setTimeout(() => {
      if (!firstRef.current?.disabled) firstRef.current?.focus();
      else panelRef.current?.querySelector<HTMLButtonElement>("button")?.focus();
    }, 50);
    return () => window.clearTimeout(t);
  }, [open, initial]);

  useEffect(() => {
    if (!open) return undefined;
    previousFocus.current = document.activeElement as HTMLElement;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !savingRef.current) closeRef.current();
      if (e.key === "Tab") {
        const controls = Array.from(panelRef.current?.querySelectorAll<HTMLElement>(
          'button:not(:disabled), input:not(:disabled), textarea:not(:disabled), select:not(:disabled), summary, [tabindex="0"]'
        ) || []).filter((el) => !el.closest("details:not([open])") || el.tagName === "SUMMARY");
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (e.shiftKey && (document.activeElement === first || !panelRef.current?.contains(document.activeElement))) {
          e.preventDefault(); last?.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault(); first?.focus();
        }
      }
    };
    document.body.classList.add("hkp-tax-overlay-open");
    window.addEventListener("keydown", onKey);
    const vv = window.visualViewport;
    const syncKb = () => {
      if (!vv) return;
      setViewport({ height: vv.height, top: vv.offsetTop });
    };
    vv?.addEventListener("resize", syncKb);
    vv?.addEventListener("scroll", syncKb);
    syncKb();
    return () => {
      document.body.classList.remove("hkp-tax-overlay-open");
      window.removeEventListener("keydown", onKey);
      vv?.removeEventListener("resize", syncKb);
      vv?.removeEventListener("scroll", syncKb);
      previousFocus.current?.focus?.();
    };
  }, [open]);

  useEffect(() => {
    if (open && mobile && panelRef.current?.contains(document.activeElement)) {
      (document.activeElement as HTMLElement)?.scrollIntoView?.({ block: "nearest" });
    }
  }, [viewport, open, mobile]);

  const exact = useMemo(
    () => (mode === "add" ? findExactNameMatch(form.name, definitions) : undefined),
    [form.name, definitions, mode]
  );
  const near = useMemo(
    () => (mode === "add" && !exact ? findNearNameMatches(form.name, definitions) : []),
    [form.name, definitions, exact, mode]
  );

  if (!open) return null;

  const title = mode === "edit" ? customFieldsCopy.editFieldTitle : customFieldsCopy.addFieldTitle;
  const lockDefinition = mode === "edit" && form.source === "GLOBAL";

  const body = (
    <>
      <div className="hkp-tax-overlay__head">
        <h2>{title}</h2>
        <button type="button" className="hkp-tax-overlay__close" onClick={onClose} disabled={saving} aria-label="Close">
          ×
        </button>
      </div>
      <div className="hkp-tax-overlay__body">
        <Label htmlFor="hkp-tax-name">{customFieldsCopy.fieldName} *</Label>
        <Input
          id="hkp-tax-name"
          innerRef={firstRef}
          value={form.name}
          disabled={lockDefinition}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
        {exact ? (
          <div className="hkp-tax-dup">
            <p className="mb-1">
              {exact.name} {customFieldsCopy.alreadyExists}
            </p>
            <Button color="primary" outline size="sm" type="button" onClick={() => onUseExisting?.(exact)}>
              {customFieldsCopy.useExisting}
            </Button>
          </div>
        ) : null}
        {near.length ? (
          <p className="text-muted small">
            Similar: {near.map((row) => row.name).join(", ")}
          </p>
        ) : null}

        <Label htmlFor="hkp-tax-desc">{customFieldsCopy.description}</Label>
        <Input
          id="hkp-tax-desc"
          type="textarea"
          rows={2}
          disabled={lockDefinition}
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
        />

        <Label htmlFor="hkp-tax-type">{customFieldsCopy.fieldType} *</Label>
        <Select
          inputId="hkp-tax-type"
          className="React_selector"
          classNamePrefix="react-select"
          isDisabled={lockDefinition}
          value={typeOptions.find((row) => row.value === form.fieldType)}
          options={typeOptions}
          onChange={(opt: any) => setForm({ ...form, fieldType: opt?.value || "TEXT" })}
          menuPortalTarget={panelRef.current || undefined}
          menuPosition="fixed"
          styles={{ menuPortal: (base) => ({ ...base, zIndex: 1300 }) }}
        />

        {mode === "edit" && !lockDefinition ? <p className="text-muted small mt-1">
          Type and usage can change only before this field is used. Existing values are preserved.
        </p> : null}
        <Label className="mt-2">{customFieldsCopy.usedIn} *</Label>
        <div className="hkp-tax-usedin" role="radiogroup" aria-label={customFieldsCopy.usedIn}>
          {(
            [
              ["PRODUCT", customFieldsCopy.usedInProduct, customFieldsCopy.usedInProductHelp],
              ["PROCESS", customFieldsCopy.usedInProcess, customFieldsCopy.usedInProcessHelp],
              ["BOTH", customFieldsCopy.usedInBoth, customFieldsCopy.usedInBothHelp],
            ] as Array<[TaxonomyUsedIn, string, string]>
          ).map(([value, label, help]) => (
            <label key={value} className={form.usedIn === value ? "is-active" : ""}>
              <input
                type="radio"
                name="hkp-tax-usedin"
                checked={form.usedIn === value}
                disabled={lockDefinition}
                onChange={() => setForm({ ...form, usedIn: value })}
              />
              <span>
                <strong>{label}</strong>
                <small>{help}</small>
              </span>
            </label>
          ))}
        </div>

        {isSelectType(form.fieldType) ? (
          <>
            <Label className="mt-2">{customFieldsCopy.options}</Label>
            <TaxonomyOptionEditor
              options={form.options}
              disabled={lockDefinition}
              onChange={(options) => setForm({ ...form, options })}
            />
          </>
        ) : null}

        {form.fieldType === "NUMBER" ? (
          <>
            <Label htmlFor="hkp-tax-unit">{customFieldsCopy.unit}</Label>
            <Input
              id="hkp-tax-unit"
              list="hkp-tax-unit-list"
              value={form.unit}
              disabled={lockDefinition}
              onChange={(e) => setForm({ ...form, unit: e.target.value })}
            />
            <datalist id="hkp-tax-unit-list">
              {unitSuggestions.map((unit) => (
                <option key={unit} value={unit} />
              ))}
            </datalist>
          </>
        ) : null}

        <Label className="mt-3 d-flex align-items-center gap-2">
          <Input
            type="switch"
            checked={form.requiredOnPublish}
            onChange={(e) => setForm({ ...form, requiredOnPublish: e.target.checked })}
          />
          {customFieldsCopy.requiredOnPublish}
        </Label>

        <Label htmlFor="hkp-tax-visibility" className="mt-2">{customFieldsCopy.visibility}</Label>
        <Input
          id="hkp-tax-visibility"
          type="select"
          value={form.visibility}
          onChange={(e) => setForm({ ...form, visibility: e.target.value as TaxonomyVisibility })}
        >
          <option value="INTERNAL">{customFieldsCopy.visibilityInternal}</option>
          <option value="PUBLIC">{customFieldsCopy.visibilityPublic}</option>
        </Input>

        <details className="hkp-tax-advanced">
          <summary>{customFieldsCopy.advanced} — {customFieldsCopy.tags}</summary>
          <Input
            aria-label={customFieldsCopy.tags}
            value={form.tags}
            disabled={lockDefinition}
            placeholder="leather, footwear"
            onChange={(e) => setForm({ ...form, tags: e.target.value })}
          />
        </details>

        <p className="text-muted small mt-2 mb-0">
          {customFieldsCopy.source}: {form.source === "GLOBAL" ? customFieldsCopy.sourceGlobal : customFieldsCopy.sourceStudio}
        </p>
      </div>
      <div className="hkp-tax-overlay__foot">
        {error ? <p className="RequiredField mb-0 hkp-tax-overlay__error">{error}</p> : null}
        <Button color="secondary" outline type="button" onClick={onClose} disabled={saving}>
          {customFieldsCopy.cancel}
        </Button>
        <Button
          color="primary"
          type="button"
          disabled={saving || form.id !== initial?.id || !trimTaxonomyInput(form.name) || !!exact
            || (isSelectType(form.fieldType) && !form.options.some((option) => trimTaxonomyInput(option.label)))}
          onClick={() => onSave(form)}
        >
          {customFieldsCopy.saveField}
        </Button>
      </div>
    </>
  );

  return (
    <div className={`hkp-tax-overlay ${mobile ? "is-mobile" : "is-desktop"}`}
      style={mobile && viewport ? { top: viewport.top, height: viewport.height, bottom: "auto" } : undefined}
      role="dialog" aria-modal="true" aria-label={title}>
      <button type="button" className="hkp-tax-overlay__backdrop" aria-label="Close" disabled={saving} tabIndex={-1} onClick={onClose} />
      <div className="hkp-tax-overlay__panel" ref={panelRef}>{body}</div>
    </div>
  );
};

export default TaxonomyFieldOverlay;
export type { FormState };
